// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, has, defer } from 'lodash/fp';
import { push } from 'react-router-redux';
import { withProps, withState, withHandlers } from '../../../../../node_module_clones/recompose';

// Utils
import { hasChanged } from '../../../../utils/react/propsComparisons';

// Selectors
import elementSearchPopupSelector from './elementSearchPopupSelector';
import { getClosestPermissionIdForElementIdSelector } from '../../../../utils/permissions/permissionsSelector';

// Actions
import {
    searchPopupSubmit,
    searchPopupClear,
    searchPopupSetQuery,
} from '../../../../components/search/searchPopupActions';

import { closePopup, openPopup } from '../../../../components/popupPanel/popupActions';
import { fetchElementsForceSkeletons } from '../../../../element/elementService';
import getBoardResultUrl from './getBoardResultUrl';
import { fetchNavigationHistory } from '../../../../user/navigationHistory/navigationHistoryActions';
import { currentUserNavigate } from '../../../../user/currentUserActions';
import { getCurrentBoardId } from '../../../../reducers/currentBoardId/currentBoardIdSelector';

// Components
import ListFocusManager from '../../../../components/lists/ListFocusManager';
import searchFormInputManager from '../../../../components/search/searchFormInputManager';
import SearchPopupMain from '../../../../components/search/SearchPopupMain';
import SearchForm from '../../../../components/search/form/SearchForm';
import ElementSearchResult from './ElementSearchResult';
import SearchIndexObserver from './SearchIndexObserver';

// Constants
import { INPUT_FOCUS_INDEX } from '../../../../components/search/searchPopupConstants';
import { UserNavigationOperation } from '../../../../../common/users/userNavigationTypes';
import { SEARCH_RESULT_TYPE } from './elementSearchConstants';
import { PopupIds } from '../../../../components/popupPanel/popupConstants';

// Styles
import './ElementSearchPopup.scss';

const getId = get('id');
const hasId = has('id');

const resultsHaveChanged = hasChanged('searchResultsList');
const currentBoardIdHasChanged = hasChanged('currentBoardId');

const mapDispatchToProps = (dispatch) => ({
    dispatchFetchHistory: () => dispatch(fetchNavigationHistory()),
    setQuery: (query) => dispatch(searchPopupSetQuery(query)),
    submitSearch: (query) => dispatch(searchPopupSubmit(query)),
    clearSearch: () => dispatch(searchPopupClear()),
    closeSearch: () => dispatch(closePopup(PopupIds.SEARCH)),
    openQuickNotes: () => dispatch(openPopup(PopupIds.QUICK_NOTES)),
    fetchElements: (elementIds) => dispatch(fetchElementsForceSkeletons({ elementIds })),
    handleResultReturnKey: (result, searchQuery) =>
        dispatch((_, getState) => {
            if (!result) return;

            const state = getState();
            const elementId = result.id;
            const permissionId = getClosestPermissionIdForElementIdSelector()(state, { elementId });
            const currentBoardId = getCurrentBoardId(state);

            return dispatch(
                push(
                    getBoardResultUrl({
                        result,
                        searchQuery,
                        permissionId,
                        currentBoardId,
                    }),
                ),
            );
        }),
    dispatchCurrentUserNavigate: ({ newBoardId, previousBoardId }) =>
        dispatch(
            currentUserNavigate({
                newBoardId,
                previousBoardId,
                operation: UserNavigationOperation.SEARCH,
            }),
        ),
});

@connect(elementSearchPopupSelector, mapDispatchToProps)
@withProps((ownProps) => ({
    initialFocusIndex: INPUT_FOCUS_INDEX,
    listSize: ownProps.searchResultsList ? ownProps.searchResultsList.length : 0,
}))
@ListFocusManager() // eslint-disable-line new-cap
@withState('searchedCurrentBoardId', 'setSearchedCurrentBoardId', (props) => props.currentBoardId)
@withState('hasNavigated', 'setHasNavigated', false)
@withHandlers({
    handleResultReturnKey:
        ({ currentBoardId, quickNotesBoardId, handleResultReturnKey, closeSearch, openQuickNotes }) =>
        (selectedResult, searchQuery) => {
            if (selectedResult.resultType === SEARCH_RESULT_TYPE.QUICK_NOTES) {
                defer(() => {
                    closeSearch();
                    openQuickNotes();
                });
            }

            if (selectedResult.resultType === SEARCH_RESULT_TYPE.CURRENT_BOARD) {
                defer(() => {
                    closeSearch();
                });
            }

            return handleResultReturnKey(selectedResult, searchQuery);
        },
    setQuery:
        ({ currentBoardId, setQuery, setSearchedCurrentBoardId, setHasNavigated }) =>
        (query) => {
            setHasNavigated(false);
            setSearchedCurrentBoardId(currentBoardId);
            setQuery(query);
        },
    submitSearch:
        ({ currentBoardId, submitSearch, setSearchedCurrentBoardId, setHasNavigated }) =>
        (query) => {
            setHasNavigated(false);
            setSearchedCurrentBoardId(currentBoardId);
            submitSearch(query);
        },
})
@searchFormInputManager
class ElementSearchPopup extends React.Component {
    constructor(props) {
        super(props);

        this.searchInputRef = React.createRef();
        this.initialCurrentBoardId = props.currentBoardId;
    }

    componentDidMount() {
        this.props.dispatchFetchHistory();
    }

    componentWillReceiveProps(nextProps) {
        const { searchResultsList } = nextProps;

        if (resultsHaveChanged(this.props, nextProps)) {
            this.props.fetchElements(searchResultsList.filter(hasId).map(getId));
        }

        if (currentBoardIdHasChanged(this.props, nextProps)) {
            this.props.setHasNavigated(true);
        }
    }

    /**
     * User navigations aren't recorded while the user is browsing through search results.
     * If the search popup is closed while viewing search results and the board ID is not the same board
     * as when the search popup was opened, then we should record the navigation.
     */
    componentWillUnmount() {
        if (this.initialCurrentBoardId === this.props.currentBoardId) return;

        this.props.dispatchCurrentUserNavigate({
            newBoardId: this.props.currentBoardId,
            previousBoardId: this.initialCurrentBoardId,
        });
    }

    clearSearch = () => {
        const { clearSearch, cancelAutoSubmitDebounce } = this.props;
        clearSearch();
        cancelAutoSubmitDebounce();
    };

    focusOnInput = () => {
        this.searchInputRef.current && this.searchInputRef.current.focus();
    };

    render() {
        const { handleKeyDown, searchedCurrentBoardId } = this.props;

        return (
            <div onKeyDown={handleKeyDown}>
                <SearchIndexObserver />
                <SearchForm
                    {...this.props}
                    ref={this.searchInputRef}
                    setQuery={this.setQuery}
                    submitSearch={this.submitSearch}
                    clearSearch={this.clearSearch}
                    searchInputPlaceholder="Search your boards..."
                />
                <SearchPopupMain
                    {...this.props}
                    focusOnInput={this.focusOnInput}
                    searchedCurrentBoardId={searchedCurrentBoardId}
                    ResultComponent={ElementSearchResult}
                />
            </div>
        );
    }
}

ElementSearchPopup.propTypes = {
    fetchElements: PropTypes.func,
    handleKeyDown: PropTypes.func,
    searchQuery: PropTypes.string,
    viewedBoardIds: PropTypes.array,
    currentBoardId: PropTypes.string,
    rootBoardId: PropTypes.string,
    results: PropTypes.object,
    resultsTotal: PropTypes.number,
    boardSummaries: PropTypes.array,
    boardSummariesAreFetched: PropTypes.bool,
    searchResultsList: PropTypes.array,
    loading: PropTypes.bool,
    focusedIndex: PropTypes.number,
    hasMoreResults: PropTypes.bool,

    submitSearch: PropTypes.func,
    closeSearch: PropTypes.func,
    clearSearch: PropTypes.func,
    setQuery: PropTypes.func,
    navigateToSearchResult: PropTypes.func,
    loadMoreResults: PropTypes.func,
    disabled: PropTypes.bool,
    cancelAutoSubmitDebounce: PropTypes.func,

    setSearchedCurrentBoardId: PropTypes.func,
    searchedCurrentBoardId: PropTypes.string,

    setHasNavigated: PropTypes.func,
    dispatchFetchHistory: PropTypes.func,
    dispatchCurrentUserNavigate: PropTypes.func,
};

export default ElementSearchPopup;
